/**
 * Game clock helpers.
 *
 * The clock counts down, and the API takes a goal's time (and a penalty's
 * off time) as "MM:SS", which its GameClock concern expands to 00:MM:SS.
 * Minutes therefore cannot reach 60: the API reads the value back with
 * strftime('%M:%S'), so 60 or more would roll into an hour and return a
 * different time than was entered.
 */
export const MAX_CLOCK_MINUTES = 59;
export const MAX_CLOCK_SECONDS = 59;

export interface ClockValue {
  minutes: string;
  seconds: string;
}

export const EMPTY_CLOCK: ClockValue = { minutes: "", seconds: "" };

/** Split an "MM:SS" value from the API into its editable parts. */
export function splitClock(formatted: string | null | undefined): ClockValue {
  if (!formatted) {
    return EMPTY_CLOCK;
  }

  const [minutes = "", seconds = ""] = formatted.split(":");
  return { minutes, seconds };
}

/** Render the clock as the zero-padded "MM:SS" the API expects. */
export function formatClock({ minutes, seconds }: ClockValue): string {
  return `${minutes.padStart(2, "0")}:${seconds.padStart(2, "0")}`;
}

/** Keep only the digits a clock field can hold. */
export function sanitizeClockPart(value: string): string {
  return value.replace(/\D/g, "").slice(0, 2);
}

/** The reason this clock cannot be saved, or null when it is valid. */
export function clockError({ minutes, seconds }: ClockValue): string | null {
  if (minutes === "" || seconds === "") {
    return "Enter the minutes and seconds shown on the clock.";
  }
  if (Number(minutes) > MAX_CLOCK_MINUTES) {
    return `Minutes must be ${MAX_CLOCK_MINUTES} or less.`;
  }
  if (Number(seconds) > MAX_CLOCK_SECONDS) {
    return `Seconds must be ${MAX_CLOCK_SECONDS} or less.`;
  }
  return null;
}

export function isClockComplete(value: ClockValue): boolean {
  return clockError(value) === null;
}
